import type { ComponentType } from 'react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { matchPath } from 'react-router-dom';
import {
  faBriefcase,
  faBuilding,
  faHome,
  faMoneyBill,
  faShop,
  faUserGroup,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import { UserRole } from '@core/generated/gql-types';
import type { PortalAccessContext, PortalAudience } from '@/access';
import { buildPortalAccessContext, getPortalAudience } from '@/access';
import {
  About,
  Bills,
  ClientPage,
  Clients,
  ConfirmSignUp,
  Dashboard,
  ForgotPassword,
  JobCreate,
  JobPage,
  Jobs,
  Login,
  Reports,
  ResetPassword,
  SetNewPassword,
  SignUp,
  Sites,
  ClientUsers,
  WorkerPage,
  Workers,
} from '@/pages';

export type PortalRouteDef = {
  id: string;
  path: string;
  component: ComponentType;
  audiences: PortalAudience[];
  adminOnly?: boolean;
  menu?: {
    title: string;
    icon: IconDefinition;
    order: number;
    audiences: PortalAudience[];
    adminOnly?: boolean;
  };
};

export type PublicRouteDef = {
  id: string;
  path: string;
  component: ComponentType;
};

export const DEFAULT_HOME_BY_AUDIENCE: Record<PortalAudience, string> = {
  client: '/jobs',
  tenant: '/dashboard',
};

export const PUBLIC_AUTH_ROUTES: PublicRouteDef[] = [
  { id: 'login', path: '/login', component: Login },
  { id: 'signup', path: '/signup', component: SignUp },
  {
    id: 'forgot-password',
    path: '/forgot-password',
    component: ForgotPassword,
  },
  { id: 'reset-password', path: '/reset-password', component: ResetPassword },
  { id: 'confirm-signup', path: '/confirm-signup', component: ConfirmSignUp },
  {
    id: 'set-new-password',
    path: '/set-new-password',
    component: SetNewPassword,
  },
];

export const PUBLIC_ROUTES: PublicRouteDef[] = [
  ...PUBLIC_AUTH_ROUTES,
  { id: 'about', path: '/about', component: About },
];

export const PROTECTED_APP_ROUTES: PortalRouteDef[] = [
  {
    id: 'dashboard',
    path: '/dashboard',
    component: Dashboard,
    audiences: ['tenant'],
    menu: {
      title: 'Dashboard',
      icon: faHome,
      order: 0,
      audiences: ['tenant'],
    },
  },
  {
    id: 'jobs',
    path: '/jobs',
    component: Jobs,
    audiences: ['client', 'tenant'],
    menu: {
      title: 'Jobs',
      icon: faBriefcase,
      order: 1,
      audiences: ['client', 'tenant'],
    },
  },
  {
    id: 'jobs-new',
    path: '/jobs/new',
    component: JobCreate,
    audiences: ['client', 'tenant'],
  },
  {
    id: 'jobs-detail',
    path: '/jobs/:id',
    component: JobPage,
    audiences: ['client', 'tenant'],
  },
  {
    id: 'sites',
    path: '/sites',
    component: Sites,
    audiences: ['client'],
    adminOnly: true,
    menu: {
      title: 'Sites',
      icon: faShop,
      order: 2,
      audiences: ['client'],
      adminOnly: true,
    },
  },
  {
    id: 'users',
    path: '/users',
    component: ClientUsers,
    audiences: ['client'],
    adminOnly: true,
    menu: {
      title: 'Users',
      icon: faUsers,
      order: 3,
      audiences: ['client'],
      adminOnly: true,
    },
  },
  {
    id: 'workers',
    path: '/workers',
    component: Workers,
    audiences: ['tenant'],
    menu: {
      title: 'Workers',
      icon: faUserGroup,
      order: 2,
      audiences: ['tenant'],
    },
  },
  {
    id: 'workers-detail',
    path: '/workers/:id',
    component: WorkerPage,
    audiences: ['tenant'],
  },
  {
    id: 'clients',
    path: '/clients',
    component: Clients,
    audiences: ['tenant'],
    menu: {
      title: 'Clients',
      icon: faBuilding,
      order: 3,
      audiences: ['tenant'],
    },
  },
  {
    id: 'clients-detail',
    path: '/clients/:id',
    component: ClientPage,
    audiences: ['tenant'],
  },
  {
    id: 'reports',
    path: '/reports',
    component: Reports,
    audiences: ['tenant'],
  },
  {
    id: 'bills',
    path: '/bills',
    component: Bills,
    audiences: ['tenant'],
    adminOnly: true,
    menu: {
      title: 'Bills',
      icon: faMoneyBill,
      order: 4,
      audiences: ['tenant'],
      adminOnly: true,
    },
  },
];

export type { PortalAccessContext, PortalAudience } from '@/access';
export { buildPortalAccessContext, getPortalAccessFromRole } from '@/access';

const hasAudienceAdminAccess = (
  audience: PortalAudience,
  access: Pick<PortalAccessContext, 'isClientAdmin' | 'isPortalAdmin'>,
): boolean =>
  audience === 'client' ? access.isClientAdmin : access.isPortalAdmin;

export const isRouteAccessible = (
  route: PortalRouteDef,
  access: PortalAccessContext,
): boolean => {
  if (!route.audiences.includes(access.audience)) {
    return false;
  }

  if (route.adminOnly && !hasAudienceAdminAccess(access.audience, access)) {
    return false;
  }

  return true;
};

export const getProtectedRoutesForPortal = (
  portalAudience: PortalAudience,
  access: Pick<PortalAccessContext, 'isPortalAdmin' | 'isClientAdmin'>,
): PortalRouteDef[] =>
  PROTECTED_APP_ROUTES.filter(
    (route) =>
      route.audiences.includes(portalAudience) &&
      (!route.adminOnly || hasAudienceAdminAccess(portalAudience, access)),
  );

/** All protected app routes registered in the single shared router tree. */
export const getRegisteredProtectedRoutes = (
  access: Pick<
    PortalAccessContext,
    'audience' | 'isPortalAdmin' | 'isClientAdmin'
  >,
): PortalRouteDef[] =>
  PROTECTED_APP_ROUTES.filter(
    (route) =>
      !route.adminOnly || hasAudienceAdminAccess(access.audience, access),
  );

export type SidebarMenuItem = {
  id: string;
  path: string;
  icon: IconDefinition;
  title: string;
};

export const getSidebarMenuItems = (
  access: PortalAccessContext,
): SidebarMenuItem[] => {
  if (!access.isClientPortal && !access.isTenantPortal) {
    return [];
  }

  const audience = access.audience;

  return PROTECTED_APP_ROUTES.filter((route) => {
    if (!route.menu?.audiences.includes(audience)) {
      return false;
    }

    if (route.menu.adminOnly && !hasAudienceAdminAccess(audience, access)) {
      return false;
    }

    return true;
  })
    .sort((a, b) => (a.menu?.order ?? 0) - (b.menu?.order ?? 0))
    .map((route) => ({
      id: route.id,
      path: route.path,
      icon: route.menu!.icon,
      title: route.menu!.title,
    }));
};

export const getDefaultHomePath = (
  access: Pick<PortalAccessContext, 'audience' | 'isClientPortal'>,
): string => DEFAULT_HOME_BY_AUDIENCE[getPortalAudience(access)];

export const findMatchingProtectedRoute = (
  logicalPath: string,
): PortalRouteDef | undefined => {
  const normalizedPath = logicalPath.startsWith('/')
    ? logicalPath
    : `/${logicalPath}`;

  const routesBySpecificity = [...PROTECTED_APP_ROUTES].sort(
    (a, b) => b.path.length - a.path.length,
  );

  return routesBySpecificity.find((route) =>
    matchPath({ path: route.path, end: true }, normalizedPath),
  );
};

/** Redirect when the current path is not accessible for the user's portal role. */
export const getPortalAlignmentRedirect = (
  pathname: string,
  role: UserRole | null | undefined,
): string | undefined => {
  if (!role) {
    return undefined;
  }

  const matchedRoute = findMatchingProtectedRoute(pathname);

  if (!matchedRoute) {
    return undefined;
  }

  const access = buildPortalAccessContext(role);

  if (isRouteAccessible(matchedRoute, access)) {
    return undefined;
  }

  return getDefaultHomePath(access);
};
